import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, RefreshCw, AlertCircle } from 'lucide-react';
import '../styles/dashboard.css';

const API_BASE_URL = 'https://gnrl-bknd-server.onrender.com/api';

const Dashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState('');
  const [fileNameFilter, setFileNameFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    username: true,
    fileName: true,
    playTime: true,
    repeats: true,
    status: true,
    lastUpdated: true
  });

  // Fetch data function
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/progress/user/all`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }
      
      const result = await response.json();
      const sortedData = Array.isArray(result) ? result.sort((a, b) => 
        new Date(b.updatedAt) - new Date(a.updatedAt)
      ) : [];
      setData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      setError('Error fetching data. Please try again.');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search function
  const filterData = useCallback(() => {
    let filtered = [...data];

    if (usernameFilter.length >= 2) {
      filtered = filtered.filter(item =>
        item.username.toLowerCase().includes(usernameFilter.toLowerCase())
      );
    }

    if (fileNameFilter.length >= 2) {
      filtered = filtered.filter(item =>
        item.fileName.toLowerCase().includes(fileNameFilter.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [data, usernameFilter, fileNameFilter]);

  // Effect for initial data fetch
  useEffect(() => {
    fetchData();
  }, [refreshKey]);

  // Effect for filtering
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      filterData();
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [filterData, usernameFilter, fileNameFilter]);

  // Adjust visible columns based on screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      if (width < 576) {
        setVisibleColumns({
          username: true,
          fileName: true,
          playTime: false,
          repeats: false,
          status: true,
          lastUpdated: false
        });
      } else if (width < 768) {
        setVisibleColumns({
          username: true,
          fileName: true,
          playTime: true,
          repeats: false,
          status: true,
          lastUpdated: false
        });
      } else if (width < 992) {
        setVisibleColumns({
          username: true,
          fileName: true,
          playTime: true,
          repeats: true,
          status: true,
          lastUpdated: false
        });
      } else {
        setVisibleColumns({
          username: true,
          fileName: true,
          playTime: true,
          repeats: true,
          status: true,
          lastUpdated: true
        });
      }
    };

    // Initial call and event listener
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleUsernameFilterChange = (e) => {
    const value = e.target.value;
    setUsernameFilter(value);
    if (value.length < 2) {
      setFilteredData(data);
    }
  };

  const handleFileNameFilterChange = (e) => {
    const value = e.target.value;
    setFileNameFilter(value);
    if (value.length < 2) {
      setFilteredData(data);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setUsernameFilter('');
    setFileNameFilter('');
  };

  const downloadCSV = () => {
    try {
      const headers = ['Username', 'File Name', 'Play Time', 'Repeats', 'Status', 'Last Updated'];
      const csvContent = [
        headers.join(','),
        ...filteredData.map(item => [
          item.username,
          item.fileName,
          item.playTime,
          item.repeats,
          item.completed ? 'Completed' : 'In Progress',
          new Date(item.updatedAt).toLocaleString()
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'student_progress.csv';
      link.click();
    } catch (error) {
      setError('Error downloading data');
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-card">
        <div className="card-header">
          <h2>Student Progress Dashboard</h2>
          <div className="header-actions">
            <button 
              className="refresh-button"
              onClick={handleRefresh}
              aria-label="Refresh data"
            >
              <RefreshCw size={20} />
            </button>
            <button 
              className="download-button"
              onClick={downloadCSV}
              disabled={!filteredData.length}
              aria-label="Download as CSV"
            >
              <Download size={20} />
              <span className="button-text">Export CSV</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message" role="alert">
            <AlertCircle />
            {error}
          </div>
        )}

        <div className="filters">
          <div className="search-group">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Type at least 2 characters to filter by username..."
              value={usernameFilter}
              onChange={handleUsernameFilterChange}
              className="search-input"
              aria-label="Filter by username"
            />
            {usernameFilter && usernameFilter.length < 2 && (
              <small className="search-hint">Type at least 2 characters</small>
            )}
          </div>
          <div className="search-group">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Type at least 2 characters to filter by file name..."
              value={fileNameFilter}
              onChange={handleFileNameFilterChange}
              className="search-input"
              aria-label="Filter by file name"
            />
            {fileNameFilter && fileNameFilter.length < 2 && (
              <small className="search-hint">Type at least 2 characters</small>
            )}
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading data...</p>
            </div>
          ) : filteredData.length > 0 ? (
            <div className="table-responsive">
              <div className="table-scroll">
                <table role="grid">
                  <thead>
                    <tr>
                      {visibleColumns.username && <th scope="col" className="sticky-header">Username</th>}
                      {visibleColumns.fileName && <th scope="col" className="sticky-header">File Name</th>}
                      {visibleColumns.playTime && <th scope="col" className="sticky-header">Play Time</th>}
                      {visibleColumns.repeats && <th scope="col" className="sticky-header">Repeats</th>}
                      {visibleColumns.status && <th scope="col" className="sticky-header">Status</th>}
                      {visibleColumns.lastUpdated && <th scope="col" className="sticky-header">Last Updated</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <tr key={item._id}>
                        {visibleColumns.username && <td data-label="Username">{item.username}</td>}
                        {visibleColumns.fileName && <td data-label="File Name">{item.fileName}</td>}
                        {visibleColumns.playTime && <td data-label="Play Time">{item.playTime}</td>}
                        {visibleColumns.repeats && <td data-label="Repeats">{item.repeats}</td>}
                        {visibleColumns.status && (
                          <td data-label="Status">
                            <span className={`status-badge ${item.completed ? 'completed' : 'in-progress'}`}>
                              {item.completed ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        )}
                        {visibleColumns.lastUpdated && (
                          <td data-label="Last Updated">{new Date(item.updatedAt).toLocaleString()}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>No matching results found</p>
              <p className="empty-state-hint">
                {(usernameFilter.length >= 2 || fileNameFilter.length >= 2) 
                  ? "Try adjusting your search terms"
                  : "Type at least 2 characters to start searching"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
